import React, { useEffect, useMemo } from 'react';

import {
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';

import Button from '../../layout/components/Button';

import DeleteIcon from '../styles/images/deleteIcon.svg';
import EditIcon from '../styles/images/editIcon.svg';
import PlusIcon from '../styles/images/plusIcon.svg';

import '../styles/table.scss';

interface TableProps {
  title: string;
  data: any[];
  columns: any[];
  useRowNumbers?: boolean;
  disabled?: boolean;
  editingMode: boolean;
  addButtonText: string;
  onAddButtonClick?: () => void | null;
  onEdittingModeButtonClick?: () => void | null;
  setEditingMode: (newValue: boolean) => void;
  setIsAddModalOpen: (newValue: boolean) => void;
  setIsDeleteModalOpen: (newValue: boolean) => void;
  onEditButtonClick: (index: number) => void;
  onDeleteButtonClick: (index: number) => void;
}

export interface DeleteModalData {
  index: number | null;
  showModal: boolean;
}

export const Table: React.FC<TableProps> = ({
  title,
  data,
  columns,
  editingMode,
  useRowNumbers = false,
  addButtonText,
  onAddButtonClick = null,
  onEdittingModeButtonClick = null,
  setEditingMode,
  setIsAddModalOpen,
  setIsDeleteModalOpen,
  onEditButtonClick,
  onDeleteButtonClick,
  disabled = false,
}) => {
  const columnsMemoized = useMemo(() => {
    return [
      ...(editingMode
        ? [
            {
              id: 'delete',
              header: () => null,
              cell: ({ row }: { row: Row<any> }) => (
                <button
                  onClick={() => onDeleteButtonClick(row.index)}
                  className="delete-btn">
                  <img src={DeleteIcon} alt="delete" />
                </button>
              ),
              size: 50,
            },
            {
              id: 'edit',
              header: () => null,
              cell: ({ row }: { row: Row<any> }) => (
                <button
                  onClick={() => onEditButtonClick(row.index)}
                  className="edit-btn">
                  <img src={EditIcon} alt="edit" />
                </button>
              ),
              size: 50,
            },
          ]
        : []),
      ...(useRowNumbers
        ? [
            {
              id: 'rowNumber',
              header: () => 'No.',
              cell: ({ row }: { row: Row<any> }) => row.index + 1,
              size: 50,
            },
          ]
        : []),
      ...columns,
    ];
  }, [
    columns,
    editingMode,
    onDeleteButtonClick,
    onEditButtonClick,
    useRowNumbers,
  ]);
  const dataMemoized = useMemo(() => {
    return data;
  }, [data]);

  useEffect(() => {
    if (disabled) {
      setEditingMode(false);
    }
  }, [disabled, setEditingMode]);

  const tableInstance = useReactTable({
    columns: columnsMemoized,
    data: dataMemoized,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 150,
      minSize: 20,
    },
  });

  return (
    <>
      <div className="table-preheader">
        {title ? (
          <div className="table-preheader__title">{title}</div>
        ) : (
          <div></div>
        )}

        <div className="table-preheader__control-btns">
          {editingMode ? (
            <>
              <Button
                disabled={disabled}
                iconLeft={EditIcon}
                onClick={() => setEditingMode(false)}
                theme="light-table">
                Stop Editing
              </Button>
              <Button
                disabled={disabled}
                iconLeft={DeleteIcon}
                onClick={() => {
                  setIsDeleteModalOpen(true);
                }}
                theme="danger-table">
                Delete All
              </Button>
            </>
          ) : (
            <>
              <Button
                iconLeft={PlusIcon}
                onClick={() => {
                  if (onAddButtonClick) onAddButtonClick();
                  setIsAddModalOpen(true);
                }}>
                {addButtonText}
              </Button>
              <Button
                disabled={disabled}
                iconLeft={EditIcon}
                onClick={() => {
                  if (onEdittingModeButtonClick) onEdittingModeButtonClick();
                  setEditingMode(true);
                }}
                theme="light-table">
                Edit table
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="table-body">
        <table className="table">
          {/* <thead>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {editingMode && (
                  <>
                    <th className='editing-placeholder'></th>
                    <th className='editing-placeholder'></th>
                  </>
                )}
                {useRowNumbers && (
                  <th className='head-number-cell'>
                    <div>No.</div>
                  </th>
                )}
                
                {headerGroup.headers.map((header) => (
                  <th key={header.id} style={{ width: header.getSize() }}>
                    <div>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableInstance.getRowModel().rows.map((row, i) => (
              <tr key={row.id}>
                {editingMode && (
                  <>
                    <td 
                      className='delete-btn' 
                      onClick={disabled ? undefined : () => onDeleteButtonClick(i)}
                    >
                      <img src={DeleteIcon} alt='verticalDotsIcon' />
                    </td>
                    <td 
                      className='edit-btn' 
                      onClick={disabled ? undefined : () => onEditButtonClick(i)}
                    >
                      <img src={EditIcon} alt='verticalDotsIcon' />
                    </td>
                  </>
                )}
                {useRowNumbers && <td className='body-number-cell'>{i + 1}</td>}
                
                {row.getVisibleCells().map((cell) => (
                  <td 
                    key={cell.id} 
                    className={`body-cell ${cell.column.id}`}
                    style={{ width: cell.column.getSize() }}
                  >
                    <span>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody> */}
          <thead>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} style={{ width: header.getSize() }}>
                    <div>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {tableInstance.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`body-cell ${cell.column.id}`}
                    style={{ width: cell.column.getSize() }}>
                    <span>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Table;
